import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { TypeaheadInput, InputWrapper, IconInput } from 'components/Form';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import FieldLabel from 'components/Form/FieldLabel';
import styled from 'styled-components/macro';
import { theme } from 'styled-tools';
import { H4 } from 'components/Typography';
import ButtonWrapper from 'components/Button/ButtonWrapper';
import { SearchIcon } from 'components/Icons';
import { ListItem, WrappedPaginator } from './components';
import { getUserData } from 'utils/auth';
import debounce from 'lodash/debounce';
import get from 'lodash/get';

const PER_PAGE = 15;
const DEFAULT_ERROR_MSG = 'Something went wrong while trying to load the page. Please try again.';

const GET_MODULES = gql`
  query getModules(
    $page: Int!
    $perPage: Int!
    $market: ID
    $sortOrder: String
    $sortField: String
    $categories: [ID]
    $query: String
    $privateContent: Boolean
  ) {
    pagedModules(
      page: $page
      perPage: $perPage
      sortOrder: $sortOrder
      sortField: $sortField
      filter: { q: $query, markets: $market, categories: $categories, privateContent: $privateContent }
    ) {
      modulesCount
      modules {
        id
        title
        description
        publishingStatus
        catalogNumber
        markets
        accessLevel
        createdBy
        updatedBy
        categories
        learningCredits
        additionalCertificates
        playingTimeInSeconds
        hasIsbeCompletionCertificate
        videoThumbnailLink
        video {
          videoCaptionLink
        }
        presenters {
          id
          name
        }
        quiz {
          enabled
        }
      }
    }
  }
`;

const GET_STATIC_DATA = gql`
  query getStaticData {
    categories: allAdminCategories(sortField: "name", sortOrder: "ascending") {
      id
      name
    }
    markets {
      id
      name
    }
  }
`;

const MemberTab = styled.a`
  &:hover {
    cursor: pointer;
  }

  &.selected {
    font-weight: bold;
    border-bottom: 3px solid ${theme('colors.primary')};
  }
`;

const WorkSectorSelectWrapper = styled(ButtonWrapper)`
  display: flex;
  margin: 1rem 0;
  margin-top: 0;

  ${MemberTab} {
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-left: 0.5rem;
      margin-right: 0;
    }
  }
`;

const ModuleListWrapper = styled.div`
  margin-top: 2rem;
`;

const NoResultLabel = styled.label`
  margin-top: 2rem;
  color: ${theme('colors.darkerGray')};
`;

function buildFilters({ marketFilter, categoryFilters, hasPrivateContent }) {
  const activeFilters = {};

  if (marketFilter !== 'private') {
    activeFilters.market = marketFilter;
  }

  if (categoryFilters && categoryFilters.length > 0) {
    activeFilters.categories = categoryFilters.map(c => c.name);
  }

  if (hasPrivateContent && marketFilter === 'private') {
    activeFilters.privateContent = true;
  }

  return activeFilters;
}

function ModuleList({ title, disabled }) {
  // module list states
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  const [modules, setModules] = useState([]);
  const [totalModules, setTotalModules] = useState(0);

  // filter data
  const [categories, setCategories] = useState([]);
  const [markets, setMarkets] = useState([]);

  // filter states
  const [searchFilters, setSearchFilter] = useState();
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [marketFilter, setMarketFilter] = useState();
  const [hasPrivateContent, setHasPrivateContent] = useState(false);

  const debounceSetSearchFilter = useCallback(debounce(setSearchFilter, 500), []);

  const onSearchChange = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }

    return debounceSetSearchFilter(e.target && e.target.value);
  };

  const [getHasPrivateContent] = useLazyQuery(GET_MODULES, {
    fetchPolicy: 'no-cache',
    onCompleted: data => setHasPrivateContent(get(data, 'pagedModules.modulesCount', 0) > 0),
    onError: err => console.error(err.message),
  });

  React.useEffect(() => {
    getHasPrivateContent({
      variables: {
        page: 0,
        perPage: PER_PAGE,
        privateContent: true,
      },
    });
  }, [getHasPrivateContent]);

  useQuery(GET_STATIC_DATA, {
    onCompleted: ({ categories, markets }) => {
      setError(false);
      setCategories(categories);
      setMarkets(markets);
      setMarketFilter(get(markets, '[0].id'));
    },
    onError: err => setError(DEFAULT_ERROR_MSG),
  });

  useQuery(GET_MODULES, {
    skip: !marketFilter,
    variables: {
      page: page,
      perPage: PER_PAGE,
      sortField: 'title',
      sortOrder: 'ascending',
      query: searchFilters,
      ...buildFilters({ marketFilter, categoryFilters, hasPrivateContent }),
    },
    onCompleted: ({ pagedModules }) => {
      setError(false);
      setModules(pagedModules.modules);
      setTotalModules(pagedModules.modulesCount);
    },
    onError: err => setError(DEFAULT_ERROR_MSG),
  });

  return (
    <ModuleListWrapper>
      <H4>{title ?? 'Select Presentation to be included in this Learning Template'}</H4>
      <label>Work Sector</label>
      <WorkSectorSelectWrapper align='left'>
        {hasPrivateContent && (
          <MemberTab
            className={marketFilter === 'private' ? 'selected' : ''}
            key={`private_content`}
            active={marketFilter === 'private'}
            onClick={() => setMarketFilter('private')}
            aria-label={`Filter modules by private content`}
            tabIndex='0'
          >
            {get(getUserData(), 'memberAgency.name', 'Private Content')}
          </MemberTab>
        )}
        {markets.map(market => (
          <MemberTab
            className={marketFilter === market.id ? 'selected' : ''}
            key={market.id}
            onClick={() => setMarketFilter(market.id)}
            aria-label={`Filter modules by ${market.name}`}
            tabIndex='0'
          >
            {market.name}
          </MemberTab>
        ))}
      </WorkSectorSelectWrapper>

      <InputWrapper>
        <FieldLabel htmlFor='module-search'>Search</FieldLabel>
        <IconInput
          id='module-search'
          name='q'
          type='text'
          onChange={onSearchChange}
          onKeyPress={onSearchChange}
          icon={SearchIcon}
        />
      </InputWrapper>

      <label>Select a category</label>
      <TypeaheadInput
        name='categories'
        placeholder='Select a category'
        selected={categoryFilters}
        options={
          categories &&
          categories.map(category => {
            return { label: category.name, name: category.id };
          })
        }
        onChange={selections => setCategoryFilters(selections)}
      />

      {error || modules.length === 0 ? (
        <NoResultLabel>{error || 'No Results Found'}</NoResultLabel>
      ) : (
        <>
          <WrappedPaginator perPage={PER_PAGE} totalModules={totalModules} page={page} setPage={setPage} />
          {modules.map(module => (
            <ListItem module={module} key={module.id} disabled={disabled} />
          ))}
          <WrappedPaginator perPage={PER_PAGE} totalModules={totalModules} page={page} setPage={setPage} />
        </>
      )}
    </ModuleListWrapper>
  );
}

ModuleList.propTypes = {
  title: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ModuleList;
